import { createContext, useCallback, useContext, useMemo, type ReactNode } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { fetchSession, login as loginRequest, logout as logoutRequest } from './api'
import type { NavEntry, PlatformSettings, User } from './types'

interface AuthContextValue {
  user: User | null
  isAuthenticated: boolean
  isLoading: boolean
  nav: NavEntry[]
  /** Global platform settings from the session payload — see PlatformSettings' comment. */
  settings: PlatformSettings
  /**
   * Mirrors NewUser.has_permission() server-side: Administrator implicitly
   * holds every permission regardless of what's in `permissions` (which for
   * an admin is already every key, from get_permission_keys() — this just
   * makes the client-side check robust even if that ever changes).
   */
  hasPermission: (key: string) => boolean
  login: (email: string, password: string, recaptchaToken: string) => Promise<{ ok: boolean; message: string }>
  logout: () => Promise<void>
  /** Re-fetches the session after something outside login()/logout() (e.g. ResetPasswordPage) changes auth state server-side. */
  refreshSession: () => Promise<void>
}

const AuthContext = createContext<AuthContextValue | null>(null)

const SESSION_QUERY_KEY = ['session']

export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient()

  const { data, isLoading } = useQuery({
    queryKey: SESSION_QUERY_KEY,
    queryFn: fetchSession,
    staleTime: 60_000,
    retry: false,
  })

  const permissionSet = useMemo(() => new Set(data?.permissions ?? []), [data?.permissions])
  const user = data?.user ?? null

  const hasPermission = useCallback(
    (key: string) => {
      if (!user) return false
      if (user.is_admin || user.is_super_user) return true
      return permissionSet.has(key)
    },
    [user, permissionSet],
  )

  const login = useCallback(
    async (email: string, password: string, recaptchaToken: string) => {
      try {
        const res = await loginRequest(email, password, recaptchaToken)
        if (res.authenticated) {
          await queryClient.invalidateQueries({ queryKey: SESSION_QUERY_KEY })
          return { ok: true, message: res.message }
        }
        return { ok: false, message: res.message }
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Login failed'
        return { ok: false, message }
      }
    },
    [queryClient],
  )

  const refreshSession = useCallback(async () => {
    await queryClient.invalidateQueries({ queryKey: SESSION_QUERY_KEY })
  }, [queryClient])

  const logout = useCallback(async () => {
    await logoutRequest()
    queryClient.setQueryData(SESSION_QUERY_KEY, {
      authenticated: false,
      user: null,
      permissions: [],
      nav: [],
      settings: {},
    })
  }, [queryClient])

  const value: AuthContextValue = {
    user,
    isAuthenticated: Boolean(data?.authenticated),
    isLoading,
    nav: data?.nav ?? [],
    settings: data?.settings ?? {},
    hasPermission,
    login,
    logout,
    refreshSession,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used within AuthProvider')
  return ctx
}
